import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useRef,
    type ReactNode,
} from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

/* ─── Types ─── */
export interface AuthUser {
    walletAddress: string;
    displayName?: string;
    onboarded: boolean;
}

interface AuthContextValue {
    /** The authenticated user (null if not signed in) */
    user: AuthUser | null;
    /** JWT token for API calls */
    token: string | null;
    /** Whether we're in the process of authenticating */
    loading: boolean;
    /** Sign out: clear token + disconnect wallet */
    signOut: () => void;
    /** Whether user is authenticated */
    isAuthenticated: boolean;
}

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

/* ─── Context ─── */
const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
    return ctx;
}

/* ─── Provider ─── */
export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
    const { publicKey, disconnect, connected, wallet } = useWallet();
    const [user, setUser] = useState<AuthUser | null>(null);
    const [token, setToken] = useState<string | null>(() => {
        return localStorage.getItem("codev_token");
    });
    const [loading, setLoading] = useState(false);
    /** Guards against auto-auth re-triggering during signOut → disconnect window */
    const signingOutRef = useRef(false);

    /**
     * Authenticate by calling POST /api/auth/connect with the wallet address.
     * No signature required — just wallet connection.
     */
    const authenticate = useCallback(async (walletAddress: string) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/auth/connect`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ walletAddress }),
            });

            if (!res.ok) throw new Error("Authentication failed");

            const data = await res.json();

            // Abort if signOut was called while the fetch was in-flight
            if (signingOutRef.current) return;

            setToken(data.token);
            setUser(data.user);
            localStorage.setItem("codev_token", data.token);

            Sentry.setUser({
                id: data.user.walletAddress,
                username: data.user.displayName || undefined,
            });

            posthog.identify(data.user.walletAddress, {
                displayName: data.user.displayName || undefined,
            });
            posthog.capture("sign_in");
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Sign out — clear state + storage, disconnect wallet.
     */
    const signOut = useCallback(() => {
        signingOutRef.current = true;
        setUser(null);
        setToken(null);
        localStorage.removeItem("codev_token");
        Sentry.setUser(null);
        posthog.capture("sign_out");
        posthog.reset();
        disconnect().catch(() => { }).finally(() => {
            signingOutRef.current = false;
        });
    }, [disconnect]);

    /**
     * On mount (or token change), try to restore the session
     * by calling GET /api/auth/me with the stored token.
     */
    useEffect(() => {
        if (!token) {
            setUser(null);
            return;
        }

        let cancelled = false;

        async function restore() {
            try {
                const res = await fetch(`${API_BASE}/api/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!res.ok) {
                    // Token expired or invalid
                    localStorage.removeItem("codev_token");
                    setToken(null);
                    setUser(null);
                    return;
                }

                const userData = await res.json();
                if (!cancelled) {
                    setUser(userData);
                    Sentry.setUser({
                        id: userData.walletAddress,
                        username: userData.displayName || undefined,
                    });
                }
            } catch {
                // Backend unreachable — keep token, clear user
                if (!cancelled) setUser(null);
            }
        }

        restore();
        return () => { cancelled = true; };
    }, [token]);

    /**
     * If wallet disconnects, clear all auth state + localStorage.
     * With auto-auth on connect, there's no reason to keep stale tokens.
     */
    useEffect(() => {
        if (!connected && (user || token)) {
            setUser(null);
            setToken(null);
            localStorage.removeItem("codev_token");
            Sentry.setUser(null);
            posthog.reset();
        }
    }, [connected]);

    /**
     * Detect Phantom account switch via Wallet Standard events.
     * The wallet adapter doesn't propagate account changes, so we listen
     * directly to the Wallet Standard wallet's `change` event.
     */
    useEffect(() => {
        if (!connected || !publicKey) return;

        const registry = (window as any).__WALLET_STANDARD__;
        if (!registry?.get) return;

        const walletName = wallet?.adapter?.name;
        if (!walletName) return;

        const standardWallets = registry.get();
        const standardWallet = standardWallets.find((w: any) => w.name === walletName);
        if (!standardWallet?.features?.["standard:events"]?.on) return;

        const currentAddress = publicKey.toBase58();

        const off = standardWallet.features["standard:events"].on("change", (props: any) => {
            if ("accounts" in props) {
                const newAccount = standardWallet.accounts[0];
                if (!newAccount || newAccount.address !== currentAddress) {
                    console.log("[AuthProvider] wallet standard account change detected");
                    setUser(null);
                    setToken(null);
                    localStorage.removeItem("codev_token");
                    Sentry.setUser(null);
                    posthog.reset();
                    disconnect().catch(() => {});
                }
            }
        });

        return () => off();
    }, [connected, publicKey, wallet, disconnect]);

    /**
     * Listen directly to Phantom provider's `accountChanged` event.
     * This fires reliably in Phantom's Android in-app browser where
     * Wallet Standard events and focus/visibility checks don't work
     * (account switching is an overlay within the same WebView).
     */
    useEffect(() => {
        const provider = (window as any).phantom?.solana;
        if (!provider?.on || !connected || !publicKey) return;

        const handleAccountChanged = (newPublicKey: any) => {
            const currentAddress = publicKey.toBase58();
            const newAddress = newPublicKey?.toBase58?.();

            if (!newPublicKey || newAddress !== currentAddress) {
                console.log("[AuthProvider] phantom accountChanged event detected");
                setUser(null);
                setToken(null);
                localStorage.removeItem("codev_token");
                Sentry.setUser(null);
                posthog.reset();
                disconnect().catch(() => {});
            }
        };

        provider.on("accountChanged", handleAccountChanged);
        return () => {
            provider.removeListener?.("accountChanged", handleAccountChanged) ??
                provider.off?.("accountChanged", handleAccountChanged);
        };
    }, [connected, publicKey, disconnect]);

    /**
     * Fallback: check for account mismatch when tab regains focus.
     * Phantom's extension UI opens as a popup — when the user switches
     * accounts there and returns to the app tab, we catch the mismatch.
     */
    useEffect(() => {
        if (!connected || !publicKey) return;

        const checkAccount = () => {
            const phantom = (window as any).phantom?.solana;
            if (!phantom?.publicKey) return;

            const phantomAddress = phantom.publicKey.toBase58();
            const adapterAddress = publicKey.toBase58();

            if (phantomAddress !== adapterAddress) {
                console.log("[AuthProvider] focus check: account mismatch detected");
                setUser(null);
                setToken(null);
                localStorage.removeItem("codev_token");
                Sentry.setUser(null);
                posthog.reset();
                disconnect().catch(() => {});
            }
        };

        const onVisibility = () => {
            if (document.visibilityState === "visible") checkAccount();
        };

        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("focus", checkAccount);

        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("focus", checkAccount);
        };
    }, [connected, publicKey, disconnect]);

    /**
     * Auto-authenticate when wallet connects.
     *
     * Handles:
     * - Fresh connection → authenticate immediately
     * - Wallet switch on page refresh → clear stale session, re-authenticate
     * - Stored token for current wallet → let token restore effect handle it
     *
     * The `loading` guard prevents double calls on re-renders.
     */
    useEffect(() => {
        if (!connected || !publicKey || loading || signingOutRef.current) return;

        const connectedAddress = publicKey.toBase58();

        // Authenticated but wallet changed — clear stale session, fall through to re-auth
        if (user && token && connectedAddress !== user.walletAddress) {
            setUser(null);
            setToken(null);
            localStorage.removeItem("codev_token");
            Sentry.setUser(null);
            posthog.reset();
            // don't return — fall through to authenticate() below
        } else if (user && token) {
            // Already authenticated with correct wallet — nothing to do
            return;
        }

        // If we have a stored token, check if it belongs to the current wallet
        const storedToken = localStorage.getItem("codev_token");
        if (storedToken) {
            try {
                const payload = JSON.parse(atob(storedToken.split(".")[1]));
                if (payload.walletAddress === connectedAddress) {
                    // Token is for current wallet — token restore effect handles it
                    return;
                }
            } catch {
                // Malformed token — fall through to clear + re-auth
            }
            // Token is for wrong wallet or malformed — clear it
            localStorage.removeItem("codev_token");
            setToken(null);
        }

        // No valid session — authenticate automatically
        authenticate(connectedAddress).catch(() => {});
    }, [connected, publicKey, user, token, loading]);

    const value: AuthContextValue = {
        user,
        token,
        loading,
        signOut,
        isAuthenticated: !!user && !!token,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
